import styled, { css } from 'styled-components';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from '@marty-js/design/src/utils/translation';
import { DEVICE_SIZE, mq } from '@marty-js/design/src/utils/mq';
import dynamic from 'next/dynamic';
import { useThemeSwitcher } from '@marty-js/design/src/utils/theme-switcher';
import { useLinkComponent } from '@marty-js/design/src/utils/component';
import { formatNavLinksFromComponent } from '../../utils/headerFormatter';
import { PrimaryNavItems } from './primary-nav-items';
import { useSdkConfig } from '../../utils/config';
import { PrimarySubNav } from './primary-sub-nav';
import { UserProfile } from './user-profile';
import { AuthContext } from '../../utils/AuthContext';

const BurgerMenuIcon = dynamic(() => import('@marty-js/design/src/icons/burgerMenu'));
const Clubic = dynamic(() => import('@marty-js/design/src/icons/clubic'));
const Close = dynamic(() => import('@marty-js/design/src/icons/close'));
const Moon = dynamic(() => import('@marty-js/design/src/icons/moon'));
const Search = dynamic(() => import('@marty-js/design/src/icons/search'));
const Message = dynamic(() => import('@marty-js/design/src/icons/message'));
const BoulangerHeader = dynamic(() => import('@marty-js/design/src/icons/op-spe/boulanger-header'));
const CyberProTechUnderHeader = dynamic(() => import('@marty-js/design/src/icons/op-spe/cyber-protech'));

const HeaderContainer = styled.header`
  position: sticky;
  display: flex;
  align-items: center;
  justify-content: space-between;
  top: 0;
  width: 100%;
  background-color: var(--theme-color-background);
  transition: background-color 1s;
  z-index: 1000002;

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      background-color: var(--theme-color-background);
      position: relative;
      justify-content: center;
      margin-bottom: 32px;
    `,
  )}
`;

const NavBar = styled.div`
  display: flex;
  background: #ff0037;
  color: var(--theme-color-title);
  width: 100%;
  height: 45px;
  justify-content: space-between;
  align-items: center;
  gap: var(--spacer-s);

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      height: initial;
      background: transparent;
      max-width: 1250px;
      padding: var(--spacer-s);
    `,
  )}
`;

const BurgerIconClose = styled.div`
  cursor: pointer;
  display: flex;
  margin-left: var(--spacer-s);

  path {
    fill: var(--theme-palette-white);
  }

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      padding: 0 var(--spacer-s);
      margin-left: initial;

      &.mod-fixed {
        height: 100%;
        position: fixed;
        top: 25px;
        left: var(--spacer-xs);
        z-index: 100006;
      }

      path {
        fill: var(--theme-color-foreground);
      }
    `,
  )}
`;

const BurgerIcon = styled.div`
  cursor: pointer;
  display: flex;
  margin-left: var(--spacer-s);

  path {
    fill: var(--theme-palette-white);
  }

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      padding: 0 var(--spacer-s);
      margin-left: initial;

      &.mod-fixed {
        position: fixed;
        top: var(--spacer-s);
        left: 0;
        z-index: 100006;
      }

      path {
        fill: var(--theme-color-foreground);
      }
    `,
  )}
`;

const Logo = styled.div`
  display: flex;
  margin: 0 auto;
  text-decoration: none;
  justify-content: center;
  position: absolute;
  top: 0;
  left: 50%;
  transform: translate(-50%);

  svg {
    border-radius: 6px;
    width: 88px;
    height: 45px;
  }

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      position: relative;
      top: initial;
      left: initial;
      transform: initial;
      margin: 0;

      &.mod-fixed {
        position: fixed;
        top: var(--spacer-s);
        left: var(--spacer-l);
        z-index: 100006;
      }

      svg {
        width: 82px;
        height: 44px;
      }
    `,
  )}
`;

const ActionBarRight = styled.div`
  display: flex;
  flex-direction: row;
  gap: var(--spacer-s);
  justify-content: center;
  margin-right: var(--spacer-s);

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      margin-right: initial;
    `,
  )}
`;

const ActionBarLeft = styled.div`
  position: absolute;
  display: flex;
  flex-direction: row;
  gap: var(--spacer-s);
  top: 0;
  left: 45px;
  margin-top: var(--spacer-s-fluid);

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      position: initial;
      justify-content: center;
      margin-top: initial;
    `,
  )}
`;

const IconButton = styled.div`
  align-items: center;
  cursor: pointer;
  display: flex;
  font-weight: 600;
  max-height: 45px;
  white-space: nowrap;

  path {
    fill: var(--theme-palette-white);
  }

  &.vanilla {
    path {
      stroke: var(--theme-palette-white);
      fill: none;
    }
  }

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      path {
        fill: var(--text-color);
      }

      &.vanilla {
        path {
          fill: none;
          stroke: var(--text-color);
        }
      }

      background: var(--theme-background-contrast-background-color);
      border-radius: 6px;
      padding: var(--spacer-s);
    `,
  )}
`;

const UserIcon = styled.div`
  display: flex;

  ${mq.lte(
    DEVICE_SIZE.LARGE,
    css`
      &.mod-hidden {
        position: relative;
        top: -45px;
      }
    `,
  )}
`;

const Nav = styled.nav`
  display: none;

  &.mod-display {
    ${mq.lte(
      DEVICE_SIZE.LARGE,
      css`
        display: flex;
        position: absolute;
        width: 100%;
        padding: 0 var(--spacer-s);
        top: 45px;
        background: var(--theme-background-contrast-background-color);
        flex-direction: column;
        overflow-y: auto;
        max-height: calc(100vh - 45px);
      `,
    )}
  }

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      display: flex;
      flex-grow: 1;

      &.mod-display {
        margin-left: 100px;
      }
    `,
  )}
`;

const TopMenu = styled.ul`
  display: none;

  &.mod-display {
    ${mq.lte(
      DEVICE_SIZE.LARGE,
      css`
        display: flex;
        position: initial;
        flex-direction: column;
        gap: var(--spacer-s);
        font-size: 1.1em;
        padding: var(--spacer-s) 0;
      `,
    )}
  }

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      display: flex;
      flex-grow: 1;
      align-items: center;
      justify-content: space-between;
      padding-top: var(--spacer-s-fluid);
    `,
  )}
`;

const TopMenuItem = styled.li`
  display: flex;
  border-bottom: 1px solid #dfe3ea;
  padding-bottom: var(--spacer-s-fluid);

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      display: flex;
      flex-grow: 1;
      gap: var(--spacer-s);
      flex-direction: column;
      justify-content: space-between;
      padding-right: var(--spacer-s);
      padding-left: var(--spacer-s);
      font-weight: bold;
      border: initial;
      text-transform: uppercase;
      white-space: nowrap;
    `,
  )}
`;

const UnderMenu = styled.ul`
  display: none;

  &.mod-display {
    ${mq.lte(
      DEVICE_SIZE.LARGE,
      css`
        display: flex;
        position: initial;
        flex-direction: column;
      `,
    )}
  }

  > ul {
    margin: auto;
    max-width: 1200px;
  }

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      display: flex;
      justify-content: center;
      align-items: center;
      flex-basis: 100%;
      background-color: var(--theme-palette-dark-grey);
      color: white;
      height: 40px;
      width: 100%;
      padding: 0;
      line-height: 1;
      position: absolute;
      text-align: center;
      top: 72px;
      left: 0;
      z-index: 2;
    `,
  )}
`;

const BurgerMenu = styled.div`
  display: none;

  &.mod-display {
    display: flex;
  }

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      display: flex;
      position: fixed;
      transform: translateX(-100%);
      left: 0;
      top: 45px;

      &.mod-display {
        background: var(--theme-background-contrast-background-color);
        bottom: 0;
        box-shadow: 0 0 60px 0 rgba(0, 0, 0, 0.38);
        height: initial;
        position: fixed;
        top: 0px;
        left: 0;
        display: flex;
        flex-direction: column;
        overflow-y: auto;
        padding: var(--spacer-l) var(--spacer-s) 0;
        transform: translateX(0%);
        transition: transform 0.3s;
        width: 20%;
        z-index: 1000;
      }
    `,
  )}
`;

interface Props {
  underHeaderComponent: any;
  headerComponent: any;
  burgerComponent: any;
}

let hasStickyTopClass: boolean;
let stickyAdExists: boolean;
let menu: any;
const positionStickyAd = () => {
  if (hasStickyTopClass && stickyAdExists) {
    let menuHeight = menu?.getBoundingClientRect().height + menu?.getBoundingClientRect().top;

    const topBanner = document.getElementById('top-banner');
    let topHeight = menu.clientHeight;
    if (topBanner && topBanner?.clientHeight) {
      topHeight = topBanner.clientHeight + menu.clientHeight;
    }
    if (menuHeight <= 25) {
      menuHeight = 0;
    }
    if (document.documentElement.scrollTop < topHeight && topBanner) {
      menuHeight += 32;
    }

    document.getElementById('opd_bottomstickyad').style.setProperty('top', `${menuHeight}px`, 'important');
  }
};

if (typeof window !== 'undefined') {
  document.addEventListener('DOMContentLoaded', () => {
    menu = document?.getElementById('nav-primary');
    const observer = new MutationObserver((mutations, obs) => {
      mutations.forEach(() => {
        hasStickyTopClass = document.getElementsByClassName('odstickytop').length > 0;
        stickyAdExists = document.getElementById('opd_bottomstickyad') !== null;

        if (hasStickyTopClass && stickyAdExists && menu?.getBoundingClientRect().height > 0) {
          positionStickyAd();
          obs.disconnect();
        }
      });
    });

    const targetNode = document.body;
    const config = {
      childList: true,
      subtree: true,
    };

    observer.observe(targetNode, config);
  });
}

export const Header: React.FC<Props> = ({ headerComponent, underHeaderComponent, burgerComponent }) => {
  const t = useTranslation();
  const { avatar, username } = useContext(AuthContext);
  const LinkComponent = useLinkComponent();

  const sdkConfig = useSdkConfig();

  const navLinks = formatNavLinksFromComponent(headerComponent) ?? [];
  const headerSlug = headerComponent?.data?.slug || '';
  const subNavLinks = formatNavLinksFromComponent(underHeaderComponent) ?? [];
  const underHeaderSlug = underHeaderComponent?.data?.slug || '';
  const burgerLinks = formatNavLinksFromComponent(burgerComponent) ?? [];

  const headerOpSpe = /op-boulanger/.test(headerSlug) ? (
    <BoulangerHeader link="/awards" label="Awards 2025" scale={1.5} />
  ) : null;

  const underHeaderOpSpe = /op-protech/.test(underHeaderSlug) ? (
    <CyberProTechUnderHeader link="/cyber-pro/" label="Cyber Pro by" scale={0.4} />
  ) : null;

  const [isMenuDisplayed, setIsMenuDisplayed] = useState<boolean>(false);
  const navRef = useRef(null);
  const darkModeButtonRef = useRef(null);

  const displayMenu = useCallback(() => {
    setIsMenuDisplayed(true);
  }, []);

  const hideMenu = useCallback(() => {
    setIsMenuDisplayed(false);
  }, []);

  const [position, setPosition] = useState(null);

  useEffect(() => {
    setPosition(window.pageYOffset || document.documentElement.scrollTop);
    const handleScroll = () => {
      const moving = window.pageYOffset || document.documentElement.scrollTop;

      setPosition(moving);

      positionStickyAd();
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [position, isMenuDisplayed]);

  const { switchTheme, isDarkTheme } = useThemeSwitcher();

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        hideMenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [navRef, darkModeButtonRef, hideMenu]);

  return (
    <HeaderContainer>
      <NavBar>
        {isMenuDisplayed ? (
          <BurgerIconClose className="mod-fixed" onClick={hideMenu} aria-label={t('header.hideMenuLabel')}>
            <Close width={20} height={24} color="var(--text-color)" />
          </BurgerIconClose>
        ) : (
          <BurgerIcon onClick={displayMenu} aria-label={t('header.displayMenuLabel')}>
            <BurgerMenuIcon width={20} height={12} color="var(--text-color)" />
          </BurgerIcon>
        )}
        <Logo className={isMenuDisplayed ? 'mod-fixed' : ''}>
          <LinkComponent href="/">
            <Clubic width={82} height={44} />
          </LinkComponent>
        </Logo>
        <Nav className={isMenuDisplayed ? 'mod-display' : ''} ref={navRef}>
          <BurgerMenu className={isMenuDisplayed ? 'mod-display' : ''}>
            <PrimaryNavItems navLinks={burgerLinks} />
          </BurgerMenu>
          <TopMenu className={isMenuDisplayed ? 'mod-display' : ''}>
            {navLinks
              .filter((link) => {
                return !!link.url;
              })
              .map((navLink, index) => {
                return (
                  <TopMenuItem key={index}>
                    <LinkComponent title={navLink.title} href={navLink.url}>
                      {navLink.title}
                    </LinkComponent>
                  </TopMenuItem>
                );
              })}
            {headerOpSpe}
          </TopMenu>
          <UnderMenu className={isMenuDisplayed ? 'mod-display' : ''}>
            <PrimarySubNav navLinks={subNavLinks} />
            {underHeaderOpSpe}
          </UnderMenu>
        </Nav>
        <ActionBarLeft>
          <IconButton>
            <LinkComponent href="/search" title={t('header.search')}>
              <Search width={24} height={24} color="var(--text-color)" />
            </LinkComponent>
          </IconButton>
          <IconButton>
            <LinkComponent href="#newsletter-footer" title={t('header.newsletter')}>
              <Message width={24} height={24} color="var(--text-color-alt)" />
            </LinkComponent>
          </IconButton>
        </ActionBarLeft>
        <ActionBarRight>
          <IconButton
            ref={darkModeButtonRef}
            onClick={() => switchTheme()}
            className="vanilla"
            role="switch"
            aria-checked={isDarkTheme}
            aria-label={t('header.darkModeLabel')}
          >
            <Moon width={24} height={24} color="var(--text-color-alt)" />
          </IconButton>
          {sdkConfig.sitename === 'site-clubic' && (
            <UserIcon>
              <UserProfile icon={avatar} username={username} />
            </UserIcon>
          )}
        </ActionBarRight>
      </NavBar>
    </HeaderContainer>
  );
};
